import {
  ActionIcon,
  Group,
  NumberInput,
  Text,
  Tooltip,
} from '@mantine/core'
import { useCallback, useEffect, useState } from 'react'
import {
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
} from '@/components/icons'

export type PaginationBarProps = {
  /** Текущая страница (1-based) */
  page: number
  /** Всего страниц (минимум 1 для отображения) */
  pageCount: number
  onPageChange: (page: number) => void
  disabled?: boolean
  /** Зарезервировано для совместимости вызовов; в панели не показывается */
  total?: number
  size?: 'xs' | 'sm' | 'md'
}

/**
 * Компактная пагинация: первая / пред / поле страницы / следующая / последняя.
 */
export function PaginationBar({
  page,
  pageCount,
  onPageChange,
  disabled,
  total: _total,
  size = 'xs',
}: PaginationBarProps) {
  const safeCount = Math.max(1, pageCount)
  const clampedPage = Math.min(safeCount, Math.max(1, page))
  const [draft, setDraft] = useState(String(clampedPage))

  useEffect(() => {
    setDraft(String(clampedPage))
  }, [clampedPage])

  const go = useCallback(
    (p: number) => {
      const next = Math.min(safeCount, Math.max(1, Math.floor(p)))
      if (next !== page) onPageChange(next)
    },
    [onPageChange, page, safeCount],
  )

  const applyDraft = useCallback(() => {
    const n = parseInt(draft, 10)
    if (Number.isNaN(n)) {
      setDraft(String(clampedPage))
      return
    }
    go(n)
  }, [draft, go, clampedPage])

  const iconSize = size === 'xs' ? 14 : size === 'md' ? 18 : 16
  const isFirst = clampedPage <= 1
  const isLast = clampedPage >= safeCount

  return (
    <Group gap={4} wrap="nowrap" align="center" justify="center">
      <Tooltip label="Первая страница">
        <ActionIcon
          variant="default"
          size={size}
          radius="md"
          disabled={disabled || isFirst}
          onClick={() => go(1)}
          aria-label="Первая страница"
        >
          <IconChevronsLeft size={iconSize} stroke={1.5} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Предыдущая">
        <ActionIcon
          variant="default"
          size={size}
          radius="md"
          disabled={disabled || isFirst}
          onClick={() => go(clampedPage - 1)}
          aria-label="Предыдущая страница"
        >
          <IconChevronLeft size={iconSize} stroke={1.5} />
        </ActionIcon>
      </Tooltip>
      <NumberInput
        size={size}
        w={40}
        miw={40}
        styles={{
          input: {
            textAlign: 'center',
            paddingInline: 4,
            paddingTop: 0,
            paddingBottom: 0,
            height: 22,
            minHeight: 22,
            fontSize: 'var(--mantine-font-size-xs)',
            lineHeight: 1.2,
          },
          section: { width: 0, padding: 0, margin: 0 },
        }}
        min={1}
        max={safeCount}
        clampBehavior="strict"
        value={draft}
        onChange={(v) => setDraft(String(v ?? ''))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') applyDraft()
        }}
        onBlur={applyDraft}
        disabled={disabled}
        hideControls
        aria-label={`Страница, всего ${safeCount}`}
      />
      <Text
        size={size}
        c="dimmed"
        lh={1.2}
        fz="xs"
        style={{ whiteSpace: 'nowrap', lineHeight: '22px' }}
      >
        / {safeCount}
      </Text>
      <Tooltip label="Следующая">
        <ActionIcon
          variant="default"
          size={size}
          radius="md"
          disabled={disabled || isLast}
          onClick={() => go(clampedPage + 1)}
          aria-label="Следующая страница"
        >
          <IconChevronRight size={iconSize} stroke={1.5} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Последняя страница">
        <ActionIcon
          variant="default"
          size={size}
          radius="md"
          disabled={disabled || isLast}
          onClick={() => go(safeCount)}
          aria-label="Последняя страница"
        >
          <IconChevronsRight size={iconSize} stroke={1.5} />
        </ActionIcon>
      </Tooltip>
    </Group>
  )
}
